import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Settings, FileText, Trash2, Edit, Copy } from 'lucide-react';
import FormBuilder from '../components/FormBuilder';
import SchemaSelector from '../components/SchemaSelector';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import { ApiService } from '../utils/api';
import { clearFormDraft, closeFormDraft, listFormDrafts, markFormDraftOpen, readFormDraftOpen } from '../utils/formDraft';
import { formatDate } from '../utils/datetime';
import { useToast } from '../components/Toast';

const FormBuilderSettings = () => {
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [editingSchema, setEditingSchema] = useState(null);
  const [previewSchema, setPreviewSchema] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, schemaId: null, schemaName: '' });
  const [cloningSchemaId, setCloningSchemaId] = useState(null);
  const [draftTick, setDraftTick] = useState(0);
  const restoredOpen = useRef(false);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  // Fetch form schemas
  const { data: schemasResponse, isLoading: schemasLoading } = useQuery({
    queryKey: ['formSchemas'],
    queryFn: () => ApiService.getFormSchemas(),
  });

  // Extract schemas from paginated response
  const schemas = Array.isArray(schemasResponse?.content) ? schemasResponse.content : [];
  const drafts = useMemo(() => listFormDrafts(), [draftTick, showFormBuilder]);
  const draftKey = editingSchema?.schemaId || 'new';

  useEffect(() => {
    if (restoredOpen.current || showFormBuilder) return;
    const open = readFormDraftOpen();
    if (!open) return;
    if (open !== 'new' && schemasLoading) return;
    restoredOpen.current = true;
    if (open === 'new') {
      setEditingSchema(null);
      setShowFormBuilder(true);
      return;
    }
    const found = schemas.find((schema) => schema.schemaId === open);
    if (found) {
      setEditingSchema(found);
      setShowFormBuilder(true);
    }
  }, [schemas, schemasLoading, showFormBuilder]);
  

  // Save schema mutation
  const saveSchemaMutation = useMutation({
    mutationFn: (schemaData) => {
      if (editingSchema) {
        return ApiService.updateFormSchema(editingSchema.schemaId, schemaData);
      } else {
        return ApiService.saveFormSchema(schemaData);
      }
    },
    onSuccess: () => {
      clearFormDraft(editingSchema?.schemaId || 'new');
      queryClient.invalidateQueries({ queryKey: ['formSchemas'] });
      setShowFormBuilder(false);
      setEditingSchema(null);
      setDraftTick((n) => n + 1);
    },
  });

  // Clone schema mutation
  const cloneSchemaMutation = useMutation({
    mutationFn: (schemaId) => ApiService.cloneFormSchema(schemaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formSchemas'] });
      queryClient.refetchQueries({ queryKey: ['formSchemas'] });
      setCloningSchemaId(null);
      showSuccess('Form schema cloned successfully');
    },
    onError: (error) => {
      console.error('Failed to clone schema:', error);
      setCloningSchemaId(null);
      showError(error?.message || 'Failed to clone form schema');
    },
  });

  // Delete schema mutation
  const deleteSchemaMutation = useMutation({
    mutationFn: (schemaId) => ApiService.deleteFormSchema(schemaId),
    onSuccess: () => {
      clearFormDraft(deleteConfirmation.schemaId);
      // Invalidate and refetch the form schemas
      queryClient.invalidateQueries({ queryKey: ['formSchemas'] });
      queryClient.refetchQueries({ queryKey: ['formSchemas'] });
      // Close the confirmation dialog
      setDeleteConfirmation({ isOpen: false, schemaId: null, schemaName: '' });
    },
    onError: (error) => {
      console.error('Failed to delete schema:', error);
      // Close the confirmation dialog even on error
      setDeleteConfirmation({ isOpen: false, schemaId: null, schemaName: '' });
    },
  });

  const handleCreateNew = () => {
    setEditingSchema(null);
    setShowFormBuilder(true);
    markFormDraftOpen('new');
  };

  const handleEditSchema = (schema) => {
    setEditingSchema(schema);
    setShowFormBuilder(true);
    markFormDraftOpen(schema.schemaId);
  };

  const handleSaveSchema = (schemaData) => {
    saveSchemaMutation.mutate(schemaData);
  };

  const handleCloneSchema = (schema) => {
    if (!schema?.schemaId || cloneSchemaMutation.isPending) return;
    setCloningSchemaId(schema.schemaId);
    cloneSchemaMutation.mutate(schema.schemaId);
  };

  const handleDeleteSchema = (schemaId, schemaName) => {
    setDeleteConfirmation({
      isOpen: true,
      schemaId,
      schemaName
    });
  };

  const confirmDelete = () => {
    if (deleteConfirmation.schemaId) {
      deleteSchemaMutation.mutate(deleteConfirmation.schemaId);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, schemaId: null, schemaName: '' });
  };

  const handlePreviewSchema = (schema) => {
    setPreviewSchema(schema);
  };

  const closeFormBuilder = () => {
    closeFormDraft();
    setShowFormBuilder(false);
    setEditingSchema(null);
    setDraftTick((n) => n + 1);
  };

  const closePreview = () => {
    setPreviewSchema(null);
  };

  if (showFormBuilder) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            {editingSchema ? 'Edit Form Schema' : 'Create New Form Schema'}
          </h1>
          <Button variant="outline" onClick={closeFormBuilder}>
            Back to Schemas
          </Button>
        </div>
        
        <FormBuilder
          initialSchema={editingSchema || { name: '', fields: [] }}
          draftKey={draftKey}
          onSave={handleSaveSchema}
          onPreview={handlePreviewSchema}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Form Builder Settings</h1>
          <p className="text-gray-600 mt-1">
            Create and manage reusable form schemas for your events
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="w-4 h-4 mr-2" />
          Create New Schema
        </Button>
      </div>

      {drafts.new && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-950">
            Unsaved new form{drafts.new.schema?.name ? `: ${drafts.new.schema.name}` : ''}. A refresh keeps it in this browser.
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreateNew}>Continue</Button>
            <Button size="sm" variant="outline" onClick={() => { clearFormDraft('new'); setDraftTick((n) => n + 1); }}>Discard</Button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <FileText className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{Array.isArray(schemas) ? schemas.length : 0}</p>
                <p className="text-gray-600">Total Schemas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Settings className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {Array.isArray(schemas) ? schemas.reduce((sum, schema) => sum + (schema.fields?.length || 0), 0) : 0}
                </p>
                <p className="text-gray-600">Total Fields</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <FileText className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {Array.isArray(schemas) ? schemas.filter(schema => schema.fields?.some(field => field.required)).length : 0}
                </p>
                <p className="text-gray-600">Schemas with Required Fields</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Schemas List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Form Schemas</CardTitle>
        </CardHeader>
        <CardContent>
          {schemasLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading schemas...</p>
            </div>
          ) : !Array.isArray(schemas) || schemas.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Form Schemas</h3>
              <p className="text-gray-500 mb-4">Create your first form schema to get started.</p>
              <Button onClick={handleCreateNew}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Schema
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {Array.isArray(schemas) ? schemas.map((schema) => (
                <div
                  key={schema.schemaId}
                  className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900">{schema.name}</h3>
                        {drafts[schema.schemaId] && (
                          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Unsaved draft</span>
                        )}
                        <span className="text-sm text-gray-500">
                          {schema.fields?.length || 0} fields
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Created: {formatDate(schema.createdAt)}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {schema.fields?.slice(0, 5).map((field, index) => (
                          <span
                            key={index}
                            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                          >
                            {field.type}
                          </span>
                        ))}
                        {schema.fields?.length > 5 && (
                          <span className="text-xs text-gray-500">
                            +{schema.fields.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreviewSchema(schema)}
                      >
                        Preview
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditSchema(schema)}
                        title="Edit schema"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCloneSchema(schema)}
                        disabled={cloneSchemaMutation.isPending}
                        title="Clone schema"
                      >
                        {cloneSchemaMutation.isPending && cloningSchemaId === schema.schemaId ? (
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSchema(schema.schemaId, schema.name)}
                        className="text-red-600 hover:text-red-700"
                        disabled={deleteSchemaMutation.isPending}
                        title="Delete schema"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Modal */}
      {previewSchema && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">Preview: {previewSchema.name}</CardTitle>
                <Button variant="ghost" onClick={closePreview}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {previewSchema.fields?.map((field, index) => (
                  <div key={index} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    
                    {field.type === 'text' && (
                      <div className="h-10 bg-gray-100 border border-gray-300 rounded-md flex items-center px-3">
                        <span className="text-gray-500 text-sm">{field.placeholder || 'Text input'}</span>
                      </div>
                    )}
                    
                    {field.type === 'email' && (
                      <div className="h-10 bg-gray-100 border border-gray-300 rounded-md flex items-center px-3">
                        <span className="text-gray-500 text-sm">{field.placeholder || 'Email input'}</span>
                      </div>
                    )}
                    
                    {field.type === 'textarea' && (
                      <div className="h-20 bg-gray-100 border border-gray-300 rounded-md flex items-center px-3">
                        <span className="text-gray-500 text-sm">{field.placeholder || 'Text area'}</span>
                      </div>
                    )}
                    
                    {field.type === 'dropdown' && (
                      <div className="h-10 bg-gray-100 border border-gray-300 rounded-md flex items-center px-3">
                        <span className="text-gray-500 text-sm">
                          {field.options?.length ? `${field.options.length} options` : 'Dropdown'}
                        </span>
                      </div>
                    )}
                    
                    {field.type === 'file' && (
                      <div className="h-16 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center">
                        <span className="text-gray-500 text-sm">File upload</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Form Schema"
        message={`Are you sure you want to delete "${deleteConfirmation.schemaName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteSchemaMutation.isPending}
      />
    </div>
  );
};

export default FormBuilderSettings;
